import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bold,
  Italic,
  Underline,
  Type,
  Palette,
  Sparkles,
  X,
  ChevronDown,
  Variable,
  Minus,
  Plus
} from 'lucide-react';
import { StableInput, StableTextarea } from './StableInput';

interface TemplateVariable {
  name: string;
  label?: string;
  type: 'text' | 'number' | 'date' | 'boolean' | 'select' | 'multiselect' | 'image' | 'color' | 'url';
  options?: string[];
  defaultValue?: any;
  description?: string;
}

interface GlobalTypography {
  fontFamily: string;
  fontSize: number;
  fontWeight: string;
  lineHeight: number;
  letterSpacing: number;
  color: string;
}

interface TextFormattingWrapperProps {
  content: string;
  onChange: (content: string) => void;
  style?: React.CSSProperties;
  className?: string;
  placeholder?: string;
  inputType?: 'text' | 'textarea';
  variables?: TemplateVariable[];
  globalTypography?: GlobalTypography;
  onFontSizeChange?: (fontSize: number) => void;
}

// Predefined color palettes
const TEXT_COLORS = [
  '#000000', '#374151', '#DC2626', '#EA580C', '#D97706', '#65A30D',
  '#059669', '#0891B2', '#2563EB', '#7C3AED', '#C026D3', '#BE185D',
];

const HIGHLIGHT_COLORS = [
  'transparent', '#FEF3C7', '#DBEAFE', '#D1FAE5', '#FED7D7', '#E0E7FF',
  '#F3E8FF', '#FCE7F3', '#FDF2F8', '#F0FDF4', '#EFF6FF', '#FFFBEB',
];

export const TextFormattingWrapper: React.FC<TextFormattingWrapperProps> = ({
  content,
  onChange,
  style = {},
  className = '',
  placeholder = 'Enter text...',
  inputType = 'text',
  variables = [],
  globalTypography,
  onFontSizeChange
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const [showToolbar, setShowToolbar] = useState(false);
  const [toolbarPosition, setToolbarPosition] = useState({ x: 0, y: 0 });
  const [showTextColors, setShowTextColors] = useState(false);
  const [showHighlightColors, setShowHighlightColors] = useState(false);
  const [showVariables, setShowVariables] = useState(false);
  const [selectedText, setSelectedText] = useState('');
  const [selectionStart, setSelectionStart] = useState(0);
  const [selectionEnd, setSelectionEnd] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [isClickingVariable, setIsClickingVariable] = useState(false);

  // Extract plain text from HTML content for the input
  const plainText = content.replace(/<[^>]*>/g, '');

  // Check if content has HTML formatting
  const hasFormatting = content !== plainText;

  // Combine global typography with individual element style
  const combinedStyle = useMemo(() => {
    if (!globalTypography) {
      return style;
    }

    // Only apply global typography for properties that are not explicitly set in the individual style
    const result = { ...style };

    // Apply global typography only if the individual style doesn't have that property
    if (!style.fontFamily) result.fontFamily = globalTypography.fontFamily;
    if (!style.fontSize) result.fontSize = `${globalTypography.fontSize}px`;
    if (!style.fontWeight) result.fontWeight = globalTypography.fontWeight;
    if (!style.lineHeight) result.lineHeight = globalTypography.lineHeight;
    if (!style.letterSpacing) result.letterSpacing = `${globalTypography.letterSpacing}px`;
    if (!style.color) result.color = globalTypography.color;

    return result;
  }, [globalTypography, style]);

  // Debug: Log variables prop


  // Enhanced variable insertion function that handles selected text replacement
  const insertVariable = useCallback((variableName: string) => {
    const input = inputRef.current;
    if (!input) return;

    // Use stored selection positions from the toolbar
    let start = selectionStart;
    let end = selectionEnd;

    // If no stored selection, get current selection from input
    if (start === end) {
      start = input.selectionStart || 0;
      end = input.selectionEnd || 0;
    }

    // Use plainText (derived from content prop) for replacement
    const currentValue = plainText;
    const variableText = `{{${variableName}}}`;

    // Create new content with variable replacing selected text
    const newValue =
      currentValue.substring(0, start) +
      variableText +
      currentValue.substring(end);

    // CRITICAL FIX: Force update the input value directly and trigger change event
    // This bypasses the StableInput's debouncing and local state management
    input.value = newValue;

    // Trigger a synthetic input event to force StableInput to update its local state
    const inputEvent = new Event('input', { bubbles: true });
    input.dispatchEvent(inputEvent);

    // Call onChange to update the parent component
    onChange(newValue);

    // Close variables dropdown and toolbar
    setShowVariables(false);
    setShowToolbar(false);
    setSelectedText('');
    setSelectionStart(0);
    setSelectionEnd(0);

    // Set cursor position after variable
    setTimeout(() => {
      if (input) {
        input.focus();
        const newCursorPos = start + variableText.length;
        input.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 10);
  }, [onChange, plainText, selectedText, selectionStart, selectionEnd]);

  // Reset editing state when content changes from outside
  useEffect(() => {
    if (!isEditing && !isClickingVariable) {
      setShowToolbar(false);
      setShowVariables(false);
      setSelectedText('');
      setSelectionStart(0);
      setSelectionEnd(0);
    }
  }, [content, isEditing, isClickingVariable]);

  // Handle text selection
  const handleSelection = useCallback(() => {
    // Use a small delay to ensure selection is updated
    setTimeout(() => {
      if (!containerRef.current) return;

      if (isEditing) {
        // Handle selection in input/textarea
        const input = containerRef.current.querySelector('input, textarea') as HTMLInputElement | HTMLTextAreaElement;
        if (!input) return;

        const start = input.selectionStart || 0;
        const end = input.selectionEnd || 0;
        const selected = plainText.substring(start, end);

        if (selected.trim() && start !== end) {
          setSelectedText(selected);
          setSelectionStart(start);
          setSelectionEnd(end);

          // Calculate toolbar position
          const inputRect = input.getBoundingClientRect();
          const toolbarWidth = 320;
          const toolbarHeight = 48;
          const padding = 8;

          let x = inputRect.left + (inputRect.width / 2) - (toolbarWidth / 2);
          let y = inputRect.top - toolbarHeight - padding;

          // Keep toolbar within viewport
          const viewportWidth = window.innerWidth;
          if (x < padding) x = padding;
          if (x + toolbarWidth > viewportWidth - padding) {
            x = viewportWidth - toolbarWidth - padding;
          }
          if (y < padding) {
            y = inputRect.bottom + padding;
          }

          setToolbarPosition({ x, y });
          setShowToolbar(true);
        } else {
          setShowToolbar(false);
          setSelectedText('');
        }
      } else {
        // Handle selection in formatted display
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) {
          setShowToolbar(false);
          return;
        }

        const selectedText = selection.toString().trim();
        if (selectedText) {
          setSelectedText(selectedText);

          // Calculate position based on selection
          const range = selection.getRangeAt(0);
          const rect = range.getBoundingClientRect();
          const toolbarWidth = 320;
          const toolbarHeight = 48;
          const padding = 8;

          let x = rect.left + (rect.width / 2) - (toolbarWidth / 2);
          let y = rect.top - toolbarHeight - padding;

          // Keep toolbar within viewport
          const viewportWidth = window.innerWidth;
          if (x < padding) x = padding;
          if (x + toolbarWidth > viewportWidth - padding) {
            x = viewportWidth - toolbarWidth - padding;
          }
          if (y < padding) {
            y = rect.bottom + padding;
          }

          setToolbarPosition({ x, y });
          setShowToolbar(true);
        } else {
          setShowToolbar(false);
        }
      }
    }, 10);
  }, [plainText, isEditing]);

  // Store current formatting state for selected text
  const [currentFormats, setCurrentFormats] = useState({
    bold: false,
    italic: false,
    underline: false,
    color: '',
    backgroundColor: ''
  });

  // Helper function to detect current formatting when text is selected
  const detectCurrentFormatting = useCallback(() => {
    if (!isEditing || !selectedText) return;

    // Reset formats
    const formats = {
      bold: false,
      italic: false,
      underline: false,
      color: '',
      backgroundColor: ''
    };

    // For now, we'll track formats as we apply them
    // In a more sophisticated implementation, we'd parse the HTML around the selection
    setCurrentFormats(formats);
  }, [isEditing, selectedText]);

  // Update current formatting when selection changes
  useEffect(() => {
    detectCurrentFormatting();
  }, [detectCurrentFormatting]);

  // Helper function to build combined HTML with multiple formats
  const buildFormattedHTML = useCallback((text: string, formats: any) => {
    let result = text;

    // Apply inline styles first (color and background)
    const inlineStyles = [];
    if (formats.color) inlineStyles.push(`color: ${formats.color}`);
    if (formats.backgroundColor && formats.backgroundColor !== 'transparent') {
      inlineStyles.push(`background-color: ${formats.backgroundColor}`);
    }

    if (inlineStyles.length > 0) {
      result = `<span style="${inlineStyles.join('; ')}">${result}</span>`;
    }

    // Apply structural formatting (bold, italic, underline)
    if (formats.underline) result = `<u>${result}</u>`;
    if (formats.italic) result = `<em>${result}</em>`;
    if (formats.bold) result = `<strong>${result}</strong>`;

    return result;
  }, []);

  // Apply formatting to selected text
  const applyFormatting = useCallback((formatType: string, value?: string) => {
    if (!selectedText) return;

    if (isEditing && selectionStart !== selectionEnd) {
      const beforeText = plainText.substring(0, selectionStart);
      const afterText = plainText.substring(selectionEnd);

      // Create a copy of current formats to modify
      const newFormats = { ...currentFormats };

      // Apply new formatting while preserving existing styles
      switch (formatType) {
        case 'bold':
          newFormats.bold = !newFormats.bold; // Toggle bold
          break;
        case 'italic':
          newFormats.italic = !newFormats.italic; // Toggle italic
          break;
        case 'underline':
          newFormats.underline = !newFormats.underline; // Toggle underline
          break;
        case 'color':
          newFormats.color = value || '';
          break;
        case 'highlight':
          newFormats.backgroundColor = value === 'transparent' ? '' : (value || '');
          break;
        case 'clear':
          // Clear all formatting
          newFormats.bold = false;
          newFormats.italic = false;
          newFormats.underline = false;
          newFormats.color = '';
          newFormats.backgroundColor = '';
          break;
      }

      // Update current formats state
      setCurrentFormats(newFormats);

      // Build the new formatted text
      let formattedText;
      if (formatType === 'clear' || (!newFormats.bold && !newFormats.italic && !newFormats.underline && !newFormats.color && !newFormats.backgroundColor)) {
        // No formatting, use plain text
        formattedText = selectedText;
      } else {
        // Build combined formatting
        formattedText = buildFormattedHTML(selectedText, newFormats);
      }

      // Create new content with formatting
      const newContent = beforeText + formattedText + afterText;
      onChange(newContent);

      // Exit editing mode to show formatted result
      setIsEditing(false);
    } else if (!isEditing) {
      // Apply formatting to selected text in formatted display
      // For now, just switch to editing mode
      setIsEditing(true);
    }

    // Close toolbar
    setShowToolbar(false);
    setShowTextColors(false);
    setShowHighlightColors(false);
    setShowVariables(false);
  }, [selectedText, selectionStart, selectionEnd, plainText, onChange, isEditing, currentFormats, buildFormattedHTML]);

  // Formatting handlers
  const handleBold = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    applyFormatting('bold');
  }, [applyFormatting]);

  const handleItalic = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    applyFormatting('italic');
  }, [applyFormatting]);

  const handleUnderline = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    applyFormatting('underline');
  }, [applyFormatting]);

  const handleTextColor = useCallback((color: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    applyFormatting('color', color);
  }, [applyFormatting]);

  const handleHighlightColor = useCallback((color: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    applyFormatting('highlight', color);
  }, [applyFormatting]);

  const handleClearFormatting = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    applyFormatting('clear');
  }, [applyFormatting]);

  const handleCloseToolbar = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowToolbar(false);
    setShowTextColors(false);
    setShowHighlightColors(false);
    setShowVariables(false);
  }, []);

  // Font size handling functions
  const getCurrentFontSize = useCallback(() => {
    // Try to get font size from individual style first, then from global typography, then default
    if (style?.fontSize) {
      const fontSize = typeof style.fontSize === 'string'
        ? parseInt(style.fontSize.replace('px', ''))
        : style.fontSize;
      return fontSize || 16;
    }
    if (globalTypography?.fontSize) {
      return globalTypography.fontSize;
    }
    return 16;
  }, [style, globalTypography]);

  const handleFontSizeChange = useCallback((delta: number) => {
    const currentSize = getCurrentFontSize();
    const newSize = Math.max(8, Math.min(200, currentSize + delta));

    if (selectedText && containerRef.current) {
      // Apply font size change to the selected text
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const span = document.createElement('span');
        span.style.fontSize = `${newSize}px`;

        try {
          range.surroundContents(span);
          const newContent = containerRef.current.innerHTML;
          onChange(newContent);
        } catch (e) {
          // If surroundContents fails, create a new span with the selected content
          const selectedContent = range.extractContents();
          span.appendChild(selectedContent);
          range.insertNode(span);
          const newContent = containerRef.current.innerHTML;
          onChange(newContent);
        }
      }
    } else if (onFontSizeChange) {
      // Apply font size change to the entire text block
      onFontSizeChange(newSize);
    }
  }, [getCurrentFontSize, selectedText, onChange, onFontSizeChange]);

  // Handle input change
  const handleInputChange = useCallback((value: string) => {
    // Only update if the plain text actually changed
    // This preserves formatting when just switching between edit/display modes
    const currentPlainText = content.replace(/<[^>]*>/g, '');
    if (value !== currentPlainText) {
      // If user is typing new content, update with plain text
      onChange(value);
    }
  }, [onChange, content]);

  return (
    <>
      <div ref={containerRef} className="relative w-full h-full">
        {isEditing ? (
          // Show input for editing - keep no-drag to prevent dragging while editing
          inputType === 'textarea' ? (
            <StableTextarea
              ref={inputRef}
              value={plainText}
              onChange={handleInputChange}
              onBlur={() => {
                // Delay closing to allow variable clicks to process
                setTimeout(() => {
                  if (!isClickingVariable) {
                    setIsEditing(false);
                  }
                }, 100);
              }}
              placeholder={placeholder}
              className={`no-drag ${className}`}
              style={{
                ...combinedStyle,
                width: '100%',
                height: '100%',
                border: 'none',
                outline: 'none',
                background: 'transparent',
                wordWrap: 'break-word',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                whiteSpace: 'pre-wrap',
                overflow: 'visible',
                resize: 'none',
              }}
              onMouseUp={handleSelection}
              onKeyUp={handleSelection}
              rows={3}
              autoFocus
            />
          ) : (
            <StableInput
              ref={inputRef}
              value={plainText}
              onChange={handleInputChange}
              onBlur={() => {
                // Delay closing to allow variable clicks to process
                setTimeout(() => {
                  if (!isClickingVariable) {
                    setIsEditing(false);
                  }
                }, 100);
              }}
              placeholder={placeholder}
              className={`no-drag ${className}`}
              style={{
                ...combinedStyle,
                width: '100%',
                border: 'none',
                outline: 'none',
                background: 'transparent',
              }}
              onMouseUp={handleSelection}
              onKeyUp={handleSelection}
              autoFocus
            />
          )
        ) : (
          // Show formatted content - FIXED: removed no-drag to allow block dragging
          <div
            className={`${className} cursor-pointer select-none`}
            style={{
              ...combinedStyle,
              minHeight: inputType === 'textarea' ? '60px' : '20px',
              padding: '8px',
              border: '1px solid transparent',
              borderRadius: '4px',
              wordWrap: 'break-word',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
              whiteSpace: 'pre-wrap',
              overflow: 'visible',
            }}
            onDoubleClick={(e) => {
              e.stopPropagation();
              setIsEditing(true);
            }}
            onMouseUp={handleSelection}
            dangerouslySetInnerHTML={{
              __html: content || `<span style="color: #9CA3AF;">${placeholder}</span>`
            }}
          />
        )}
      </div>

      {/* Floating Toolbar */}
      {showToolbar && createPortal(
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-lg"
            style={{
              left: toolbarPosition.x,
              top: toolbarPosition.y,
            }}
            onMouseDown={(e) => e.preventDefault()}
          >
            <div className="flex items-center p-2 space-x-1">
              {/* Selection indicator */}
              <div className="text-xs text-gray-500 px-2 py-1 bg-gray-50 rounded mr-2">
                "{selectedText.substring(0, 20)}{selectedText.length > 20 ? '...' : ''}"
              </div>

              {/* Basic Formatting */}
              <button
                onMouseDown={handleBold}
                className={`p-2 rounded-md transition-colors ${
                  currentFormats.bold
                    ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                    : 'hover:bg-gray-100'
                }`}
                title="Bold"
              >
                <Bold className="w-4 h-4" />
              </button>

              <button
                onMouseDown={handleItalic}
                className={`p-2 rounded-md transition-colors ${
                  currentFormats.italic
                    ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                    : 'hover:bg-gray-100'
                }`}
                title="Italic"
              >
                <Italic className="w-4 h-4" />
              </button>

              <button
                onMouseDown={handleUnderline}
                className={`p-2 rounded-md transition-colors ${
                  currentFormats.underline
                    ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                    : 'hover:bg-gray-100'
                }`}
                title="Underline"
              >
                <Underline className="w-4 h-4" />
              </button>

              {/* Divider */}
              <div className="w-px h-6 bg-gray-300 mx-1" />

              {/* Font Size Controls */}
              <div className="flex items-center space-x-1">
                <button
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleFontSizeChange(-2);
                  }}
                  className="p-1.5 rounded-md hover:bg-gray-100 transition-colors"
                  title="Decrease Font Size"
                >
                  <Minus className="w-3 h-3" />
                </button>

                <div className="px-2 py-1 text-xs font-medium text-gray-600 bg-gray-50 rounded min-w-[40px] text-center">
                  {getCurrentFontSize()}px
                </div>

                <button
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleFontSizeChange(2);
                  }}
                  className="p-1.5 rounded-md hover:bg-gray-100 transition-colors"
                  title="Increase Font Size"
                >
                  <Plus className="w-3 h-3" />
                </button>
              </div>

              {/* Divider */}
              <div className="w-px h-6 bg-gray-300 mx-1" />

              {/* Text Color */}
              <div className="relative">
                <button
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowTextColors(!showTextColors);
                    setShowHighlightColors(false);
                  }}
                  className={`flex items-center p-2 rounded-md transition-colors ${
                    currentFormats.color
                      ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                      : 'hover:bg-gray-100'
                  }`}
                  title="Text Color"
                >
                  <div className="relative">
                    <Type className="w-4 h-4" />
                    {currentFormats.color && (
                      <div
                        className="absolute -bottom-1 left-0 right-0 h-1 rounded"
                        style={{ backgroundColor: currentFormats.color }}
                      />
                    )}
                  </div>
                  <ChevronDown className="w-3 h-3 ml-1" />
                </button>

                {showTextColors && (
                  <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-3 z-10 min-w-[200px]">
                    <div className="text-xs font-medium text-gray-600 mb-2">Text Color</div>
                    <div className="grid grid-cols-6 gap-2">
                      {TEXT_COLORS.map((color) => (
                        <button
                          key={color}
                          onMouseDown={(e) => handleTextColor(color, e)}
                          className="w-6 h-6 rounded border border-gray-300 hover:scale-110 transition-transform"
                          style={{ backgroundColor: color }}
                          title={color}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Background/Highlight Color */}
              <div className="relative">
                <button
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowHighlightColors(!showHighlightColors);
                    setShowTextColors(false);
                  }}
                  className={`flex items-center p-2 rounded-md transition-colors ${
                    currentFormats.backgroundColor
                      ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                      : 'hover:bg-gray-100'
                  }`}
                  title="Highlight Color"
                >
                  <div className="relative">
                    <Palette className="w-4 h-4" />
                    {currentFormats.backgroundColor && (
                      <div
                        className="absolute -bottom-1 left-0 right-0 h-1 rounded"
                        style={{ backgroundColor: currentFormats.backgroundColor }}
                      />
                    )}
                  </div>
                  <ChevronDown className="w-3 h-3 ml-1" />
                </button>

                {showHighlightColors && (
                  <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-3 z-10 min-w-[200px]">
                    <div className="text-xs font-medium text-gray-600 mb-2">Highlight Color</div>
                    <div className="grid grid-cols-6 gap-2">
                      {HIGHLIGHT_COLORS.map((color, index) => (
                        <button
                          key={color}
                          onMouseDown={(e) => handleHighlightColor(color, e)}
                          className="w-6 h-6 rounded border border-gray-300 hover:scale-110 transition-transform relative"
                          style={{ backgroundColor: color }}
                          title={index === 0 ? 'No highlight' : color}
                        >
                          {index === 0 && (
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="w-4 h-px bg-red-500 rotate-45" />
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Variables */}
              {variables.length > 0 && (
                <>
                  {/* Divider */}
                  <div className="w-px h-6 bg-gray-300 mx-1" />

                  <div className="relative">
                    <button
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowVariables(!showVariables);
                        setShowTextColors(false);
                        setShowHighlightColors(false);
                      }}
                      className="flex items-center p-2 rounded-md transition-colors hover:bg-gray-100"
                      title="Insert Variable"
                    >
                      <Variable className="w-4 h-4" />
                      <ChevronDown className="w-3 h-3 ml-1" />
                    </button>

                    {showVariables && (
                      <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-2 z-10 min-w-[200px] max-h-[200px] overflow-y-auto">
                        <div className="text-xs font-medium text-gray-600 mb-2">Insert Variable</div>
                        {variables.map((variable) => (
                          <button
                            key={variable.name}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setIsClickingVariable(true);
                              insertVariable(variable.name);
                              setTimeout(() => setIsClickingVariable(false), 200);
                            }}
                            className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded-md transition-colors block"
                            title={variable.description || `Insert ${variable.name} variable`}
                          >
                            <div className="font-medium text-sm">{variable.label || variable.name}</div>
                            <div className="text-xs text-gray-500">{`{{${variable.name}}}`}</div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Divider */}
              <div className="w-px h-6 bg-gray-300 mx-1" />

              {/* Clear Formatting */}
              <button
                onMouseDown={handleClearFormatting}
                className="p-2 hover:bg-gray-100 rounded-md transition-colors"
                title="Clear Formatting"
              >
                <Sparkles className="w-4 h-4" />
              </button>

              {/* Close */}
              <button
                onMouseDown={handleCloseToolbar}
                className="p-2 hover:bg-gray-100 rounded-md transition-colors"
                title="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};

export default TextFormattingWrapper;
